import React, { FC, useRef } from "react";
import CloseButton from "components/CloseButton";
import { buildParcelsNav } from "containers/Parcels/parcelsNav";
import { List } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { useSmsNumbersEnabled } from "../../branding/useSmsNumbers";
import useFetchOnceEffect from "../../hooks/useFetchOnceEffect";
import injectReducer from "../../reduxFolder/injectReducer";
import warehouseReducer, {
  getWarehouseBranches,
} from "../../reduxFolder/reducers/warehouse";
import {
  authSelectAllowExtendedTools,
  authSelectAuthenticated,
  authSelectCanUseContainers,
} from "../../reduxFolder/selectors/globalSelectors";
import { warehouseSelectBranches } from "../../reduxFolder/selectors/warehouseSelectors";
import {
  WarehouseBranchMap,
  WarehouseBranchResponseDto,
} from "../../types/api/warehouse";
import { ChatWidget } from "../ChatWidget/ChatWidget";
import {
  ClientsIcon,
  CurrencyFillIcon,
  HeadsetFillIcon,
  HouseFillIcon,
  MailboxFillIcon,
  PackageFillIcon,
  SquaresFourFillIcon,
  ToolsIcon,
  TruckFillIcon,
} from "./IconsComponents/IconsComponents";
import { MainAppLogo } from "../MainAppLogo";
import UserInfo from "../UserInfo/UserInfo";
import {
  ChatBubbleWrapper,
  CloseNav,
  LinkItem,
  LinkLogoItem,
  LinksWrapper,
  LinkTitle,
  Nav,
  NavFooter,
  NavWrapper,
} from "./LeftMenu.styles";

interface LeftMenuProps {
  isOpen?: boolean;
  withMenu?: boolean;
  allowExtendedTools?: boolean;
  canUseContainers?: boolean;
  authenticated?: boolean | null;
  handleSetMenuOpen: (isOpen: boolean) => void;
  warehouseBranches: List<WarehouseBranchMap>;
  getWarehouseBranches: () => Promise<WarehouseBranchResponseDto>;
}

interface NavLinkClassNameProps {
  isActive: boolean;
}

const LeftMenu: FC<LeftMenuProps> = React.memo(
  ({
    warehouseBranches,
    isOpen = false,
    withMenu = false,
    handleSetMenuOpen,
    allowExtendedTools = false,
    canUseContainers = false,
    authenticated = null,
    getWarehouseBranches,
  }) => {
    const location = useLocation();
    const navigationRef = useRef<HTMLElement>(null);
    const { t } = useTranslation("common");
    const smsNumbers = useSmsNumbersEnabled();

    // LeftMenu also mounts on public routes (Layout renders it regardless of
    // withMenu), so an unauthenticated /branches call would 401 and toast
    // "You need to sign in..." right on the login page. Fetch only once the
    // session is validated.
    useFetchOnceEffect(
      authenticated === true && !warehouseBranches.size,
      getWarehouseBranches,
    );

    const isActiveHomePage = location.pathname.startsWith("/home");
    const isActiveRecipientsPage =
      location.pathname.startsWith("/books/recipients") ||
      location.pathname.startsWith("/books/passports");
    const isActiveBooksPage =
      location.pathname.startsWith("/books") && !isActiveRecipientsPage;
    const getLinkClassName =
      (isAdditionalRouteActive = false) =>
      ({ isActive }: NavLinkClassNameProps) =>
        isActive || isAdditionalRouteActive ? "active" : "";

    const parcelsNavItems = buildParcelsNav(warehouseBranches, t, {
      canUseContainers,
      branchLabelField: "sb_name",
      branchesFirst: true,
    });

    const closeMenu = () => handleSetMenuOpen(false);
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navigationRef.current &&
        navigationRef.current.contains(event.target as Node)
      )
        return;
      closeMenu();
    };

    return (
      <NavWrapper
        $withMenu={withMenu}
        $isOpen={isOpen}
        onClick={handleClickOutside}
      >
        <Nav ref={navigationRef}>
          <LinkLogoItem onClick={closeMenu} end to="/">
            <MainAppLogo />
          </LinkLogoItem>
          <LinkItem
            to="/"
            end
            title={t("pages.home")}
            onClick={closeMenu}
            className={getLinkClassName(isActiveHomePage)}
          >
            <HouseFillIcon />
            <LinkTitle>{t("pages.home")}</LinkTitle>
          </LinkItem>
          <LinkItem
            to="/mailbox"
            title={t("pages.mailbox")}
            onClick={closeMenu}
          >
            <MailboxFillIcon />
            <LinkTitle>{t("pages.mailbox")}</LinkTitle>
          </LinkItem>
          <LinkItem
            to="/parcels"
            title={t("pages.parcels")}
            onClick={closeMenu}
          >
            <PackageFillIcon />
            <LinkTitle>{t("pages.parcels")}</LinkTitle>
          </LinkItem>
          <LinksWrapper>
            {/* `end` is deliberately not forwarded: these links match by
                prefix, as they always have. */}
            {parcelsNavItems.map(({ to, label }) => (
              <LinkItem key={to} onClick={closeMenu} to={to}>
                <LinkTitle>{label}</LinkTitle>
              </LinkItem>
            ))}
          </LinksWrapper>
          <LinkItem
            to="/inbound"
            title={t("pages.inbound")}
            onClick={closeMenu}
          >
            <TruckFillIcon />
            <LinkTitle>{t("pages.inbound")}</LinkTitle>
          </LinkItem>
          {/* LinksWrapper is mobile-only by its styles — on desktop the
              tabs live in the page header, which hides below sm. */}
          <LinksWrapper>
            <LinkItem onClick={closeMenu} to="/inbound" end>
              <LinkTitle>{t("inbound.tabs.shipments")}</LinkTitle>
            </LinkItem>
            <LinkItem onClick={closeMenu} to="/inbound/addresses">
              <LinkTitle>{t("inbound.shipFromAddresses")}</LinkTitle>
            </LinkItem>
          </LinksWrapper>
          <LinkItem
            to="/books/add-funds"
            title={t("pages.books")}
            onClick={closeMenu}
            className={getLinkClassName(isActiveBooksPage)}
          >
            <CurrencyFillIcon />
            <LinkTitle>{t("pages.books")}</LinkTitle>
          </LinkItem>
          <LinksWrapper>
            <LinkItem onClick={closeMenu} to="/books/add-funds">
              <LinkTitle>{t("books.addFunds")}</LinkTitle>
            </LinkItem>
            <LinkItem onClick={closeMenu} to="/books" end>
              <LinkTitle>{t("books.transactions")}</LinkTitle>
            </LinkItem>
          </LinksWrapper>
          <LinkItem
            to="/books/recipients"
            title={t("pages.recipients")}
            onClick={closeMenu}
            className={getLinkClassName(isActiveRecipientsPage)}
          >
            <ClientsIcon />
            <LinkTitle>{t("pages.recipients")}</LinkTitle>
          </LinkItem>
          <LinksWrapper>
            <LinkItem onClick={closeMenu} to="/books/recipients">
              <LinkTitle>{t("books.recipients")}</LinkTitle>
            </LinkItem>
            <LinkItem onClick={closeMenu} to="/books/passports">
              <LinkTitle>{t("books.passports.title")}</LinkTitle>
            </LinkItem>
          </LinksWrapper>
          {smsNumbers && (
            <>
              <LinkItem
                to="/utilities"
                title={t("pages.utilities")}
                onClick={closeMenu}
              >
                <SquaresFourFillIcon />
                <LinkTitle>{t("pages.utilities")}</LinkTitle>
              </LinkItem>
              <LinksWrapper>
                <LinkItem onClick={closeMenu} to="/utilities/sms">
                  <LinkTitle>{t("sms.tab")}</LinkTitle>
                </LinkItem>
              </LinksWrapper>
            </>
          )}
          {allowExtendedTools && (
            <LinkItem
              to="/support"
              title={t("pages.support")}
              onClick={closeMenu}
            >
              <HeadsetFillIcon />
              <LinkTitle>{t("pages.support")}</LinkTitle>
            </LinkItem>
          )}
          {allowExtendedTools && (
            <>
              <LinkItem
                to="/tools"
                title={t("pages.tools")}
                onClick={closeMenu}
              >
                <ToolsIcon />
                <LinkTitle>{t("pages.tools")}</LinkTitle>
              </LinkItem>
              <LinkItem
                to="/shopify"
                title={t("pages.shopify")}
                onClick={closeMenu}
              >
                <ToolsIcon />
                <LinkTitle>{t("pages.shopify")}</LinkTitle>
              </LinkItem>
            </>
          )}
          <ChatBubbleWrapper>
            {withMenu && <ChatWidget />}
          </ChatBubbleWrapper>
          <NavFooter>
            <UserInfo onNavigate={closeMenu} />
          </NavFooter>
        </Nav>
        <CloseNav>
          <CloseButton
            aria-label={t("common.closeMenu")}
            title={t("common.closeMenu")}
            onClick={closeMenu}
          />
        </CloseNav>
      </NavWrapper>
    );
  },
);

const withReducer = injectReducer({
  key: "warehouse",
  reducer: warehouseReducer,
});

const withConnect = connect(
  createStructuredSelector({
    allowExtendedTools: authSelectAllowExtendedTools,
    canUseContainers: authSelectCanUseContainers,
    authenticated: authSelectAuthenticated,
    warehouseBranches: warehouseSelectBranches,
  }),
  {
    getWarehouseBranches,
  },
);

const enhance = compose(withReducer, withConnect);

export default enhance(LeftMenu) as unknown as React.FC<
  Pick<LeftMenuProps, "isOpen" | "withMenu" | "handleSetMenuOpen">
>;
